import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import { Link, LoaderFunction, redirect, useLoaderData, useNavigate } from "react-router-dom";

import {
  getPlatformUserMemberships,
  getPlatformUserMembershipsCancellationReasons,
  getUser,
} from "api/methods";
import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/button/Button";
import Dropdown from "atoms/dropdown/Dropdown";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";

import { isMembershipManagedExternally } from "./membershipHelper";

const load = async (platformUserMembershipId: string) => {
  const userMemberships = queryClient.fetchQuery({ ...getPlatformUserMemberships.keyed() });

  const membershipCancellationReasons = queryClient.fetchQuery({
    ...getPlatformUserMembershipsCancellationReasons.keyed(),
  });

  const userInfo = queryClient.fetchQuery({ ...getUser.keyed() });

  return waitObj({
    platformUserMembershipId,
    userMemberships,
    userInfo,
    membershipCancellationReasons,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletMembershipCancelLoader: LoaderFunction = ({
  params: { platformUserMembershipId },
}) => {
  if (!platformUserMembershipId) {
    return redirect("..");
  }

  return load(platformUserMembershipId);
};

export const WalletMembershipCancelPage = () => {
  const { t } = useTranslation();
  const {
    platformUserMembershipId,
    userMemberships: initialUserMemberships,
    userInfo,
    membershipCancellationReasons,
  } = useLoaderData() as LoaderData;

  const navigate = useNavigate();

  const { data: userMemberships } = useQuery({
    ...getPlatformUserMemberships.keyed(),
    initialData: initialUserMemberships,
  });

  const membership = userMemberships.find(
    (m) => m.platformUserMembershipId === platformUserMembershipId,
  );

  // Mirrors the "Request a freeze" gating on the manage-membership card: when
  // the membership is freeze-eligible, a save-offer step is shown between the
  // reason selection and the final cancel confirmation.
  const isFreezeOfferAvailable =
    !!membership &&
    membership.membership.allowFreeze &&
    membership.membership.maxFreezeBillingPeriods >= 1 &&
    !membership.activeMembershipFreezeId &&
    !isMembershipManagedExternally(membership.paymentProcessorSubscriptionId, membership.zenotiId);

  const cancellationReasons = membershipCancellationReasons.map((reasonInfo) => ({
    ...reasonInfo,
    name: reasonInfo.reason,
  }));

  const [selectedCancellationReasonId, setSelectedCancellationReasonId] = useState<string>();
  const selectedCancellationReason = cancellationReasons.find(
    ({ id }) => id === selectedCancellationReasonId,
  );

  const [cancellationReasonError, setCancellationReasonError] = useState<boolean>(false);

  return (
    <div className="relative flex h-full flex-col bg-white">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">
          {t("walletMembershipCancelPage.header")}
        </div>
      </TopNav>
      <div className="flex flex-col gap-y-7 bg-white px-8 pt-3 text-left">
        <div className="text-title">
          {t("walletMembershipCancelPage.confirmCancelTitle", {
            membershipName: membership?.membership.name,
          })}
        </div>
        <div className="flex flex-col gap-y-5">
          <div>
            {t("walletMembershipCancelPage.cancelNowNotice", {
              expirationDate: dayjs(membership?.expiration).format("MMM D, YYYY"),
            })}
          </div>
          <ul className="list-disc pl-5 text-[22px] text-dark-grey">
            <li>
              <span className="align-middle text-base font-normal text-black">
                {t("walletMembershipCancelPage.benefitPricing")}
              </span>
            </li>
            <li>
              <span className="align-middle text-base font-normal text-black">
                {t("walletMembershipCancelPage.benefitMembershipBenefits")}
              </span>
            </li>
            <li>
              <span className="align-middle text-base font-normal text-black">
                {t("walletMembershipCancelPage.benefitTreatmentsAndBonuses")}
              </span>
            </li>
          </ul>
          <div>{t("walletMembershipCancelPage.unclaimedTreatmentsNotice")}</div>
          <div>
            {t("walletMembershipCancelPage.confirmationEmailPrefix")}{" "}
            <span className="text-bold1">{userInfo.email}</span>
          </div>
        </div>
        <div className="flex shrink">
          <Link to="/account/details" className="flex text-bold1 text-brand-color">
            {t("walletMembershipCancelPage.changeEmailLink")}
          </Link>
        </div>
      </div>
      <div className="grow" />
      <div className="flex flex-col gap-y-3 px-7 py-5">
        <Dropdown
          options={cancellationReasons}
          value={selectedCancellationReasonId}
          setValue={(value) => {
            setSelectedCancellationReasonId(value);
            setCancellationReasonError(false);
          }}
          textValue={selectedCancellationReason?.name}
          label={t("walletMembershipCancelPage.cancellationReasonLabel")}
          invalid={cancellationReasonError}
        />
        <Button
          fullWidth
          style="dangerSolid"
          disabled={!selectedCancellationReasonId}
          onClick={() => {
            if (!selectedCancellationReasonId) {
              setCancellationReasonError(true);
              return;
            }

            navigate(
              `/wallet/memberships/manage/${platformUserMembershipId}/cancel${
                isFreezeOfferAvailable ? "/freeze-offer" : ""
              }`,
              {
                state: {
                  membershipCancellationReasonId: selectedCancellationReasonId,
                },
              },
            );
          }}
        >
          {t("walletMembershipCancelPage.confirmCancelButton")}
        </Button>
        <Button fullWidth style="secondary" onClick={() => navigate("/wallet/memberships")}>
          {t("walletMembershipCancelPage.keepMembershipButton")}
        </Button>
      </div>
    </div>
  );
};
